import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { VideoPost } from '../types/video';

const SAVED_VIDEOS_KEY = '@saved_videos';

interface SavedVideo extends VideoPost {
  savedAt: string;
}

interface SavedVideosState {
  savedVideos: SavedVideo[];
  loading: boolean;
  loadSavedVideos: () => Promise<void>;
  saveVideo: (video: VideoPost) => Promise<void>;
  unsaveVideo: (videoId: string) => Promise<void>;
  isVideoSaved: (videoId: string) => boolean;
}

export const useSavedVideosStore = create<SavedVideosState>((set, get) => ({
  savedVideos: [],
  loading: false,

  loadSavedVideos: async () => {
    try {
      set({ loading: true });
      const stored = await AsyncStorage.getItem(SAVED_VIDEOS_KEY);
      if (stored) {
        const videos = JSON.parse(stored);
        set({ savedVideos: videos });
      }
    } catch (error) {
      console.error('Failed to load saved videos:', error);
    } finally {
      set({ loading: false });
    }
  },

  saveVideo: async (video: VideoPost) => {
    try {
      const { savedVideos } = get();
      const newVideo: SavedVideo = {
        ...video,
        savedAt: new Date().toISOString(),
      };
      const updated = [newVideo, ...savedVideos.filter(v => v.id !== video.id)];
      await AsyncStorage.setItem(SAVED_VIDEOS_KEY, JSON.stringify(updated));
      set({ savedVideos: updated });
    } catch (error) {
      console.error('Failed to save video:', error);
      throw error;
    }
  },

  unsaveVideo: async (videoId: string) => {
    try {
      const { savedVideos } = get();
      const updated = savedVideos.filter(v => v.id !== videoId);
      await AsyncStorage.setItem(SAVED_VIDEOS_KEY, JSON.stringify(updated));
      set({ savedVideos: updated });
    } catch (error) {
      console.error('Failed to unsave video:', error);
      throw error;
    }
  },

  isVideoSaved: (videoId: string) => {
    const { savedVideos } = get();
    return savedVideos.some(v => v.id === videoId);
  },
}));
